import { useQuery } from "@tanstack/react-query";
import {
  fetchCitationDetail,
  fetchCitationSite,
  fetchSources,
  type SourcesQuery,
} from "../api/index.js";
import { queryKeys } from "../query-keys.js";

/**
 * Sources a page at a time, one cited page opened up, and one cited site.
 */

/** The range part of a Sources key: where `queryKeys.sources` puts it. */
const RANGE_PARTS = 6;

/**
 * Sources' summary and one page of one table. While the next page, sort or
 * search is on its way, the rows on screen stay — but only within the same
 * report, range and engine: a new range's figures are never drawn under the
 * old range's rows (frontend.md: the range belongs in the key).
 */
export function useSources(workspaceId: string, reportId: string, query: SourcesQuery) {
  const queryKey = queryKeys.sources(workspaceId, reportId, query);
  return useQuery({
    queryKey,
    queryFn: () => fetchSources(workspaceId, reportId, query),
    placeholderData: (previous, previousQuery) => {
      const before = previousQuery?.queryKey.slice(0, RANGE_PARTS) ?? [];
      const now = queryKey.slice(0, RANGE_PARTS);
      return before.length === now.length && before.every((part, at) => part === now[at])
        ? previous
        : undefined;
    },
  });
}

export function useCitationDetail(
  workspaceId: string,
  reportId: string,
  url: string | null,
  range: {
    readonly from: string;
    readonly to: string;
    readonly engine?: string;
  },
) {
  return useQuery({
    queryKey: [
      "citation-detail",
      workspaceId,
      reportId,
      url ?? "",
      range.from,
      range.to,
      range.engine ?? "all",
    ],
    queryFn: () => fetchCitationDetail(workspaceId, reportId, url ?? "", range),
    enabled: url !== null,
  });
}

export function useCitationSite(
  workspaceId: string,
  reportId: string,
  domain: string | null,
  range: {
    readonly from: string;
    readonly to: string;
    readonly engine?: string;
  },
) {
  return useQuery({
    queryKey: [
      "citation-site",
      workspaceId,
      reportId,
      domain ?? "",
      range.from,
      range.to,
      range.engine ?? "all",
    ],
    queryFn: () => fetchCitationSite(workspaceId, reportId, domain ?? "", range),
    enabled: domain !== null,
  });
}
